import { useState, type ReactNode } from "react";
import {
  CalendarClock,
  CheckCircle2,
  Download,
  PauseCircle,
  Scale,
} from "lucide-react";
import CleButton from "@cle/components/CleButton";
import RoundedCard from "@cle/components/RoundedCard";
import { useDownloadAfgorelseDocument } from "@/hooks/useAfgorelse";
import {
  AFGOERELSE_LABELS,
  type AfgorelseDetailModel,
  type AfgorelseType,
} from "@/interfaces/afgorelse";
import { formatDanishLongDate } from "@/util/date";
import { PERIODE_LOEBER_IKKE_LABEL } from "@cle/components/injunctions/afgorelseStatus";
import type { AfgorelseDetailVariant } from "./resolveAfgorelseDetailVariant";
import { formatLessons } from "@cle/utils/lessonLimits";

/**
 * Detaljesiden for en afgørelse, der ikke kan indberettes lige nu.
 *
 * Der er to grunde til det, og de betyder vidt forskellige ting for advokaten, så de
 * skal også forklares hver for sig:
 *
 * - **I bero.** Ingen frist. Indfrielsesperioden løber kun, mens advokaten er udøvende,
 *   så en deponeret beskikkelse sætter hele forløbet på pause.
 * - **Under behandling.** Fristen er fastsat, men flowet er lukket her. Det sker, når
 *   AIKA har flyttet indberetningen videre fra "frist ordinær" — typisk fordi
 *   sekretariatet har modtaget den og er ved at behandle den. Advokaten skal ikke gøre
 *   noget, og det er dét, overskriften skal sige — ikke at et felt er tomt.
 *
 * Tidligere sagde siden blot "Der er ingen åben indberetning for denne afgørelse" og
 * viste ellers ingenting. Det er rigtigt og ubrugeligt på samme tid: advokaten kan
 * hverken se sit lektionskrav, sin periode eller selve kendelsen, og får ingen anelse
 * om, hvorvidt der er noget, vedkommende selv skal gøre.
 */

const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className="cle-report-detail__reported-metric">
    <span className="cle-report-detail__reported-metric-icon">{icon}</span>
    <span>
      <span className="cle-report-detail__reported-metric-label">{label}</span>
      <span className="cle-report-detail__reported-metric-value">{value}</span>
    </span>
  </div>
);

type InactiveAfgorelseDetailViewProps = {
  afgorelseType: AfgorelseType;
  detail: AfgorelseDetailModel;
  /** Hvilken af de to passive tilstande — se resolveAfgorelseDetailVariant. */
  variant: Extract<AfgorelseDetailVariant, "bero" | "lukket" | "afsluttet">;
};

const InactiveAfgorelseDetailView = ({
  afgorelseType,
  detail,
  variant,
}: InactiveAfgorelseDetailViewProps) => {
  const labels = AFGOERELSE_LABELS[afgorelseType];
  const { mutateAsync: downloadDocument, isPending: isDownloadingDocument } =
    useDownloadAfgorelseDocument(afgorelseType);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const erIBero = variant === "bero";
  const erAfsluttet = variant === "afsluttet";

  const handleDownloadDecision = async () => {
    setDownloadError(null);
    let objectUrl: string | null = null;
    try {
      const blob = await downloadDocument(detail.id);
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        detail.decisionDocumentFileName ?? labels.downloadFallbackFileName;
      link.click();
    } catch (error) {
      setDownloadError(
        error instanceof Error && error.message
          ? error.message
          : "Dokumentet kunne ikke hentes lige nu. Prøv igen.",
      );
    } finally {
      // Revoke on the next tick so the click-triggered download has started.
      if (objectUrl) {
        const urlToRevoke = objectUrl;
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 0);
      }
    }
  };

  return (
    <div className="cle-report-detail">
      <RoundedCard
        header={
          erIBero
            ? "Afgørelsen er sat i bero"
            : erAfsluttet
              ? "Afgørelsen er afsluttet"
              : "Afgørelsen er under behandling"
        }
        description={labels.submittedSummaryDescription}
      >
        <div className="alert alert-info" role="status">
          {erIBero ? (
            <>
              <p className="mb-2">
                Du skal ikke foretage dig noget lige nu, og du mister ikke tid
                på {labels.entityName}.
              </p>
              <p className="mb-0">
                Fristen for at gennemføre og dokumentere efteruddannelsen løber
                kun, mens du er udøvende advokat. Så længe din beskikkelse er
                deponeret, står den stille. Får du beskikkelsen genudleveret,
                genoptages fristen automatisk fra det punkt, den stod på, og du
                får besked om den nye dato.
              </p>
            </>
          ) : erAfsluttet ? (
            <>
              <p className="mb-2">
                Der skal ikke indberettes noget på {labels.entityName}.
              </p>
              <p className="mb-0">
                Afgørelsen er afsluttet hos Advokatsamfundet, og forløbet er
                slut. Oplysningerne herunder står som dokumentation for, hvad
                der gjaldt.
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">Du skal ikke foretage dig noget lige nu.</p>
              <p className="mb-0">
                Fristen er fastsat, men indberetningen kan ikke udfyldes her,
                fordi afgørelsen ligger hos Advokatsamfundet. Det er normalt,
                når din dokumentation er modtaget og er ved at blive behandlet.
                Mener du, at det er en fejl, så kontakt Advokatsamfundet — husk
                at oplyse afgørelsens dato.
              </p>
            </>
          )}
        </div>

        <div className="cle-report-detail__reported-metrics">
          <InfoRow
            icon={<Scale size={18} aria-hidden strokeWidth={1.75} />}
            label="Lektionskrav"
            value={formatLessons(detail.paakraevedeLektioner)}
          />
          <InfoRow
            icon={<CalendarClock size={18} aria-hidden strokeWidth={1.75} />}
            label="Dokumentationsperiode"
            value={
              detail.periodeTilMinusEnDag
                ? `${formatDanishLongDate(detail.periodeFra)} - ${formatDanishLongDate(
                    detail.periodeTilMinusEnDag,
                  )}`
                : PERIODE_LOEBER_IKKE_LABEL
            }
          />
          <InfoRow
            icon={
              erAfsluttet ? (
                <CheckCircle2 size={18} aria-hidden strokeWidth={1.75} />
              ) : (
                <PauseCircle size={18} aria-hidden strokeWidth={1.75} />
              )
            }
            label="Indberetningsfrist"
            value={formatDanishLongDate(
              detail.frist,
              erAfsluttet ? "Ingen" : "I bero",
            )}
          />
        </div>

        <CleButton
          className="mt-3"
          disabled={isDownloadingDocument}
          type="button"
          variant="secondary"
          onClick={handleDownloadDecision}
        >
          <Download size={16} aria-hidden strokeWidth={1.75} />
          {isDownloadingDocument
            ? "Henter..."
            : `Download ${labels.downloadButtonLabel}`}
        </CleButton>
        {downloadError ? (
          <div className="alert alert-warning mt-2 mb-0" role="alert">
            {downloadError}
          </div>
        ) : null}
      </RoundedCard>
    </div>
  );
};

export default InactiveAfgorelseDetailView;
